import { PostAdd } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Icon,
  TextField,
  Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import React from 'react'
import { connect } from 'react-redux'
import { NivosModal } from 'components/index'
import {
  Operations as DocumentAdminOperations,
  operations as documentAdminOperations
} from 'states/dux/document-admin/operations'
import { Payload as DocumentAdminPayload } from 'states/dux/document-admin/types'
import {
  Operations as DocumentOperations,
  operations as documentOperations
} from 'states/dux/document/operations'
import { Payload as DocumentPayload } from 'states/dux/document/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosFolder } from 'types/nivos-folder'
import {
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  document: DocumentPayload
  documentAdmin: DocumentAdminPayload
  user: UserPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    document: DocumentOperations
    documentAdmin: DocumentAdminOperations
    user: UserOperations
  }
}

interface OwnProps {
  folder: NivosFolder
  open: boolean
  setOpen: (_: boolean) => void
  isAdmin?: boolean
}

type AppProps = StateProps & DispatchProps & OwnProps

const UploadNewFileFC: React.FC<any> = (props: AppProps) => {
  const { theme, operations, isAdmin, folder, open, setOpen } = props
  const [name, setName] = React.useState('')
  const [uploadLoading, setLoading] = React.useState(false)
  const [file, setFile] = React.useState<FileList>()
  const isMobileView = useMediaQuery('(max-width:600px)')

  const defaultErrState = {
    file: '',
    filename: ''
  }

  const [errMessage, setError] = React.useState(defaultErrState)

  const _createNewFile = (): void => {
    if (name.trim() === '') {
      setError({ ...errMessage, filename: 'Filename is required!' })
      return
    }

    if (file === undefined || file.length < 1) {
      setError({ ...errMessage, file: 'File is required!' })
      return
    }

    const formData = new FormData()
    formData.append('name', name)
    formData.append('file', file[0])
    formData.append('folder', folder.id.toString())

    setLoading(true)

    void post('/document/file/', formData, true).then(file => {
      if (Object.hasOwn(file, 'id')) {
        const parsedFile = objectifyDateStringFields(file)
        const olFolderState = { ...folder }
        olFolderState.files.push(parsedFile)
        if (isAdmin === true) {
          operations.documentAdmin.update(olFolderState)
        } else {
          operations.document.update(olFolderState)
        }
        setOpen(false)
      } else {
        // Handle error here
      }
      setLoading(false)
    })
  }

  return (
    <>
      <NivosModal
        title={'Upload file'}
        open={open}
        setOpen={setOpen}
        containerProps={{
          sx: {
            width: 'min-content',
            maxHeight: '95vh',
            overflow: 'auto',
            padding: 2
          }
        }}
      >
        <Box padding={2} minWidth={isMobileView ? '85vw' : 400}>
          <Box
            display={'flex'}
            alignItems={'center'}
            padding={2}
            justifyContent={'space-between'}
            border={errMessage.filename.trim() !== '' ? 1 : 'none'}
            borderColor={'red'}
            borderRadius={'12px'}
          >
            <Typography>Filename:</Typography>
            <TextField
              value={name}
              onChange={event => {
                if (errMessage.filename.trim() !== '') {
                  setError({ ...errMessage, filename: '' })
                }
                setName(event.currentTarget.value)
              }}
            />
          </Box>
          {errMessage.filename.trim() !== '' && (
            <Typography variant={'body2'} padding={1} color={'red'}>
              <em>{errMessage.filename}</em>
            </Typography>
          )}
          <Box
            display={'flex'}
            alignItems={'center'}
            padding={2}
            justifyContent={'space-between'}
            border={errMessage.file.trim() !== '' ? 1 : 'none'}
            borderColor={'red'}
            borderRadius={'12px'}
          >
            <Typography>File:</Typography>
            <input
              type={'file'}
              onChange={event => {
                if (event.currentTarget.files != null) {
                  if (errMessage.file.trim() !== '') {
                    setError({ ...errMessage, file: '' })
                  }
                  setFile(event.currentTarget.files)
                }
              }}
              style={{
                color: theme.secondaryColor
              }}
            />
          </Box>
          {errMessage.file.trim() !== '' && (
            <Typography variant={'body2'} padding={1} color={'red'}>
              <em>{errMessage.file}</em>
            </Typography>
          )}
          <Box marginLeft={'auto'} width={'fit-content'} padding={2}>
            <Button
              variant={'contained'}
              onClick={() => _createNewFile()}
              disabled={uploadLoading}
            >
              {uploadLoading ? <CircularProgress size={24} /> : 'Submit'}
            </Button>
          </Box>
        </Box>
      </NivosModal>
      {!(isAdmin === true) && (
        <Box
          display={'flex'}
          alignItems={'center'}
          paddingX={1}
          onClick={() => setOpen(true)}
          sx={{
            ':hover': { paddingLeft: 3 },
            cursor: 'pointer',
            transition: '0.5s'
          }}
        >
          <Icon>
            <PostAdd />
          </Icon>
          <Typography variant={'body2'}>
            Upload new file to this folder
          </Typography>
        </Box>
      )}
    </>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    document: state.document,
    user: state.user,
    documentAdmin: state.documentAdmin
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  document: documentOperations,
  user: userOperations,
  documentAdmin: documentAdminOperations
})

const UploadNewFile = connect(
  mapStateToProps,
  mapDispatchToProps
)(UploadNewFileFC) as React.FC<OwnProps>

export default UploadNewFile
